import type { Duration } from "dayjs/plugin/duration";
import { z } from "zod";
import { createLogger } from "../logger";
import { time, timeAmount } from "../time";
import { formatErrorMessage } from "../utils";

const logger = createLogger("retry");

/** How the base delay grows between attempts. */
export type BackoffStrategy =
  | { type: "fixed" }
  | { type: "linear"; factor: number }
  | { type: "exponential"; factor: number };

/** Snapshot of a retry attempt passed to lifecycle callbacks. */
export interface RetryAttemptInfo {
  /** Number of attempts so far (1 = first failure, pre-first-retry). */
  attempt: number;
  /** Total attempts that will be made (1 + maxRetries). Infinity when maxRetries is uncapped. */
  maxAttempts: number;
  /** The error that triggered this retry. */
  error: unknown;
  /** Delay that will be waited before the next attempt (ms). */
  delayMs: number;
}

export type RetryOptions = {
  /**
   * Function that determines if the error should be retried.
   */
  shouldRetry: (error: unknown) => boolean;

  /**
   * The delay in milliseconds.
   */
  delay: Duration;

  /**
   * How the delay grows between attempts. Defaults to exponential (factor 2).
   */
  backoff?: BackoffStrategy;

  /**
   * If true, adds a random +/- 20% jitter to the delay. Defaults to false.
   */
  randomJitter?: boolean;

  /**
   * Log terminal failures at error level before rethrowing — non-retryable errors, an exhausted
   * retry budget, and an exceeded cumulative-delay cap alike. Defaults to true. Callers on a
   * one-shot budget whose "failure" can be a designed outcome (e.g. an idempotent start rejected
   * as already running) or is classified and logged by the caller pass false and own all terminal
   * logging. Per-attempt retry warnings are progress, not terminal, and are always emitted.
   */
  logTerminalFailure?: boolean;

  /**
   * Called after the max-retries check passes and the delay is calculated,
   * but before the wait begins. Useful for logging or emitting metrics.
   */
  onBeforeRetry?: (info: RetryAttemptInfo) => void | Promise<void>;

  /**
   * Called after the delay wait completes, just before re-entering the loop.
   * Useful for cleanup or state updates between attempts.
   */
  onRetry?: (info: RetryAttemptInfo) => void | Promise<void>;

  /**
   * Cancels the retry loop, INCLUDING the backoff sleep.
   *
   * Without it the sleep is the hole: a server-supplied `Retry-After` becomes the delay
   * verbatim, so a caller that has given up still waits out the full interval before the
   * loop can notice. With it, an abort settles the wait immediately and throws
   * `signal.reason` — the loop stops between attempts as well as during them.
   *
   * Cancelling the in-flight ATTEMPT is the caller's job: pass the same signal into
   * whatever `fn` does (a `fetch` init, a client option). This bounds the loop, not the work.
   *
   * Omitted by default, so every existing caller keeps its current behavior exactly.
   */
  signal?: AbortSignal;
} & ({ maxRetries: number; maxDelay?: Duration } | { maxRetries?: number; maxDelay: Duration });

/** The abort reason, preserving a caller's own reason when it supplied one. */
function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

/**
 * A cancellation, under either name a signal can raise it.
 *
 * Kept local rather than imported: this module is generic and must not depend on a Medplum
 * one. `AbortSignal.timeout()` rejects with `TimeoutError`, a caller's `.abort()` with
 * `AbortError`, and neither is a terminal failure — the caller stopped this work on purpose
 * and owns reporting why.
 */
function isCancellation(error: unknown): boolean {
  const name = error instanceof Error || error instanceof DOMException ? error.name : "";
  return name === "AbortError" || name === "TimeoutError";
}

/**
 * A sleep that settles on abort instead of running the clock down.
 *
 * The listener is removed on both paths — a retry loop that outlives many aborts would
 * otherwise accumulate listeners on a long-lived signal.
 */
function sleepUnlessAborted(ms: number, signal: AbortSignal | undefined): Promise<void> {
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  if (signal.aborted) return Promise.resolve();

  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms);
    function finish(): void {
      clearTimeout(timer);
      signal?.removeEventListener("abort", finish);
      resolve();
    }
    signal.addEventListener("abort", finish, { once: true });
  });
}

/**
 * Executes a function and retries it with backoff if it fails.
 * @param fn The function to retry
 * @param options The retry options
 * @returns The result of the function
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions): Promise<T> {
  const {
    shouldRetry,
    delay,
    backoff = { type: "exponential", factor: 2 },
    maxRetries = Infinity,
    maxDelay = timeAmount(Infinity),
    randomJitter = false,
    logTerminalFailure = true,
    onBeforeRetry,
    onRetry,
    signal,
  } = options;

  const delayMillis = delay.asMilliseconds();
  const maxDelayMillis = maxDelay.asMilliseconds();

  let attempts = 0;
  let cumulativeDelay = 0;

  while (true) {
    // Checked before the attempt as well as after the sleep: a caller that aborted while a
    // lifecycle callback ran must not get one more request out of the loop.
    if (signal?.aborted) throw abortError(signal);
    try {
      const result = await fn();
      return result;
    } catch (error) {
      attempts++;
      const errorMessage = formatErrorMessage(error);

      // If the error is not retryable, rethrow it
      if (!shouldRetry(error)) {
        // A cancellation is not a terminal failure. Error-logging one would report an
        // intended stop as a fault, on every caller that passes a signal.
        if (logTerminalFailure && !isCancellation(error)) {
          logger.error(`Non-retryable error after ${attempts} attempt(s): ${errorMessage}`, {
            attempts,
            error: errorMessage,
            cumulativeDelay,
          });
        }
        throw error;
      }

      logger.warn(`Retryable error on attempt ${attempts}: ${errorMessage}`, {
        attempts,
        error: errorMessage,
      });

      // If we've exceeded the maximum number of retries, rethrow the error
      if (attempts > maxRetries) {
        if (logTerminalFailure) {
          logger.error(`Max retries (${maxRetries}) exceeded: ${errorMessage}`, {
            attempts,
            maxRetries,
            error: errorMessage,
            cumulativeDelay,
          });
        }
        throw error;
      }

      // Calculate the next delay, honoring server-provided backoff if present on the error
      let nextDelay = delayMillis;
      let jitterAmount = 0;
      let overriddenByError = false;

      const retryAfterCarrier = z
        .object({ retryAfterMs: z.number().finite().optional() })
        .safeParse(error);

      if (retryAfterCarrier.success && retryAfterCarrier.data.retryAfterMs !== undefined) {
        // Prefer server-provided backoff exactly as instructed; no jitter/backoff strategy
        nextDelay = Math.max(0, retryAfterCarrier.data.retryAfterMs);
        overriddenByError = true;
      } else {
        // Apply backoff strategy
        if (backoff) {
          if (backoff.type === "exponential") {
            nextDelay = nextDelay * backoff.factor ** (attempts - 1);
          } else if (backoff.type === "linear") {
            // Linear: base delay on first retry, then increment by (delay * factor) each attempt.
            // Consistent with exponential which also starts at base delay on attempt 1.
            nextDelay = nextDelay * (1 + backoff.factor * (attempts - 1));
          }
          // fixed: nextDelay stays as delayMillis
        }
        // Add jitter: a random +/- 20% of the calculated delay
        if (randomJitter) {
          jitterAmount = (Math.random() * 0.4 - 0.2) * nextDelay; // Random number between -0.2 and 0.2
          nextDelay += jitterAmount;
          // Ensure nextDelay is not negative if jitter made it so, though unlikely with 20% jitter
          nextDelay = Math.max(0, nextDelay);
        }
      }

      logger.debug("Applying delay", {
        baseDelay: delayMillis,
        backoffType: backoff?.type ?? "fixed",
        attempt: attempts,
        calculatedDelay: overriddenByError ? undefined : nextDelay - jitterAmount, // Log only when not overridden
        jitterApplied: overriddenByError ? false : randomJitter,
        jitterAmount: overriddenByError ? 0 : jitterAmount,
        finalDelay: nextDelay,
        overrideFromError: overriddenByError,
      });

      // If the next delay would exceed the maximum cumulative delay, rethrow the error
      if (cumulativeDelay + nextDelay > maxDelayMillis) {
        if (logTerminalFailure) {
          logger.error(
            `Max cumulative delay (${maxDelayMillis}ms) would be exceeded, aborting: ${errorMessage}`,
            { attempts, cumulativeDelay, maxDelayMillis, nextDelay },
          );
        }
        throw error;
      }

      const maxAttempts = maxRetries === Infinity ? Infinity : maxRetries + 1;
      const attemptInfo: RetryAttemptInfo = {
        attempt: attempts,
        maxAttempts,
        error,
        delayMs: nextDelay,
      };

      // Lifecycle callbacks must never abort the retry loop
      try {
        await onBeforeRetry?.(attemptInfo);
      } catch (callbackError) {
        logger.warn("onBeforeRetry callback threw — ignoring", { callbackError });
      }

      // Wait for the current delay, or until the caller gives up — whichever comes first.
      await sleepUnlessAborted(nextDelay, signal);
      cumulativeDelay += nextDelay;
      if (signal?.aborted) {
        if (logTerminalFailure) {
          logger.warn(`Retry loop aborted after ${attempts} attempt(s): ${errorMessage}`, {
            attempts,
            cumulativeDelay,
          });
        }
        throw abortError(signal);
      }

      try {
        await onRetry?.(attemptInfo);
      } catch (callbackError) {
        logger.warn("onRetry callback threw — ignoring", { callbackError });
      }
    }
  }
}

/**
 * Parse an RFC 9110 Retry-After header into milliseconds.
 * Supports both delta-seconds (e.g. "120") and HTTP-date (IMF-fixdate) values.
 */
export function parseRetryAfterMs(retryAfterHeader: string | undefined): number | undefined {
  if (!retryAfterHeader) return undefined;

  const trimmed = retryAfterHeader.trim();
  // delta-seconds
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isFinite(seconds) ? seconds * 1000 : undefined;
  }

  // HTTP-date
  const httpDate = time(trimmed);
  if (!httpDate.isValid()) return undefined;

  const delta = httpDate.diff();
  return Math.max(0, delta);
}
